'use client'

import React, { useContext, useEffect, useRef, useState } from 'react'
import { useStore } from 'zustand'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { extractFieldIds } from '@/shared/containers/QuestionnairePageContainer'
import {
  type ConsultationRequestStage,
  ConsultationRequestFormContext,
} from '@/features/consultations/request/ConsultationRequestFormProvider'
import {
  ConsultationQuestionnaireValidator,
  createConsultationQuestions,
} from '@/lib/questionnaires/GeneralConsultation'
import { ConsultationQuestionnaireFields } from './ConsultationQuestionnaireFields'

type QuestionnaireStage = Extract<
  ConsultationRequestStage,
  { type: 'QUESTIONNAIRE' }
>

type Props = {
  stage: QuestionnaireStage
}

const ValidationSchema = ConsultationQuestionnaireValidator

export default function ConsultationQuestionnaireStageContainer({
  stage,
}: Props) {
  const store = useContext(ConsultationRequestFormContext)
  if (!store)
    throw new Error(
      'ConsultationQuestionnaireStageContainer must be used within a ConsultationRequestFormProvider',
    )

  const onNext = useStore(store, (s) => s.onNext)
  const onPrev = useStore(store, (s) => s.onPrev)
  const questionnaireFormValues = useStore(
    store,
    (s) => s.questionnaireForm.values,
  )
  const updateQuestionnaireFormValues = useStore(
    store,
    (s) => s.updateQuestionnaireFormValues,
  )

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onChange',
    defaultValues: questionnaireFormValues,
    resolver: yupResolver(ValidationSchema, { abortEarly: false }),
  })

  const { watch, trigger, getValues, setValue } = methods
  const advancing = useRef(false)
  const [isAdvancing, setIsAdvancing] = useState(false)

  // Validate the form on mount, and persist values in the request store as
  // patients answer each question.
  useEffect(() => {
    trigger().then(() => {})

    const { unsubscribe } = watch((value) => {
      updateQuestionnaireFormValues(ValidationSchema.cast(value))
    })

    return () => unsubscribe()
  }, [trigger, updateQuestionnaireFormValues, watch])

  const currentStep = createConsultationQuestions(
    stage.page,
    questionnaireFormValues,
  )
  if (
    currentStep === 'upload-supporting-document' ||
    currentStep === 'personal-information' ||
    currentStep === 'call-arrangement'
  )
    return null

  const fieldIds = extractFieldIds(currentStep) as Array<
    keyof typeof questionnaireFormValues
  >

  async function goForward() {
    if (advancing.current) return
    advancing.current = true
    setIsAdvancing(true)
    try {
      for (const name of fieldIds) {
        setValue(name, getValues(name), { shouldTouch: true })
      }
      if (await trigger(fieldIds, { shouldFocus: true })) onNext()
    } finally {
      advancing.current = false
      setIsAdvancing(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void goForward()
        }}
      >
        <ConsultationQuestionnaireFields
          currentStep={currentStep}
          firstPage={stage.page === 0}
          onBack={onPrev}
          busy={isAdvancing}
        />
      </form>
    </FormProvider>
  )
}
